import { Box, Button, Tooltip, useTheme } from '@mui/material';
import React, { forwardRef, useCallback } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';

import SlideUpList from '../../../animations/SlideUpList';
import { PER_PAGE_OPTIONS } from '../../../constants/table';
import useClearFiltersCallback from '../../../hooks/useClearFiltersCallback';
import useCopyClipboard from '../../../hooks/useCopyClipboard';
import { IReport, ReportTypesEnum } from '../../../types/reports';
import { IFiltersRef } from '../../../types/tables';
import { getClaimUrl, isValidScheduledEngagement } from '../../../utils/reports';
import { EngagementCard } from '../../index';
import PaginatedList, { IPaginatedListRef } from '../../PaginatedList/PaginatedList';
import { PageContent, PaginationControls, TableFilteredEmptyPlaceholder, TableViewLoader } from '../../_mui';
import { ReportsContextMenu } from '../components/ReportsContextMenu';
import { useReportsContextMenu } from '../hooks/useReportsContextMenu';

import styles from './ReportsGridView.module.scss';

interface IProps {
  totalCount: number;
  onChange: (event: { page: number; setPage: (p: number) => void }) => void;
  isFinalReport: boolean;
  isReportsFetching: boolean;
  onCardClick: (uid: string) => void;
  onCopyBtnClick: (e: React.MouseEvent<HTMLElement>) => void;
  filtersRef: React.RefObject<IFiltersRef>;
  items: IReport[] | null;
  activeTab: ReportTypesEnum;
  pageSize: number;
  onPageSizeChange?: (newPageSize: number) => void;
  page?: number;
}

const ReportsGridView = forwardRef<IPaginatedListRef<IReport>, IProps>(
  (
    {
      totalCount,
      onChange,
      isFinalReport,
      isReportsFetching,
      activeTab,
      filtersRef,
      items,
      pageSize,
      onPageSizeChange,
      page,
    },
    paginatedListRef,
  ) => {
    const theme = useTheme();

    const {
      menuAnchorEl,
      selectedItem,
      isNonCancelingReportStatus,
      handleMenuOpen,
      handleMenuClose,
      handleCancelReport,
      handleDeleteReport,
      canDeleteReport,
      canCancelReport,
      handleRowClick,
      canCopyTrackingUrl,
      canCopyShippingUrl,
      isMoreActionsDisabled,
    } = useReportsContextMenu({
      activeTab,
    });

    const { setCopied } = useCopyClipboard({ successDuration: 1000, toastDuration: 1000 });
    const handleClearFilters = useClearFiltersCallback(filtersRef);

    const renderContent = useCallback(
      (reportsList: IReport[]) => {
        return !reportsList || !reportsList.length ? (
          <TableFilteredEmptyPlaceholder itemType="delivery reports" onClearFilters={handleClearFilters} />
        ) : (
          <PageContent>
            <div className={styles.list}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(300px, 1fr))',
                  gap: '24px',
                  '@media (max-width: 1199px)': {
                    gridTemplateColumns: 'repeat(1, minmax(300px, 1fr))',
                  },
                }}
              >
                <SlideUpList
                  items={reportsList.map((report: IReport) => {
                    const moreActionsDisabled = isMoreActionsDisabled(report);

                    return {
                      key: report.uid,
                      renderer: (
                        <EngagementCard
                          engagement={report}
                          wide
                          className={styles.card}
                          controls={[
                            !isValidScheduledEngagement(report) && !!getClaimUrl(report) && (
                              <Button
                                sx={{ padding: '4px 10px' }}
                                variant="outlined"
                                key={`copy-claim-${report.uid}`}
                                onClick={() => setCopied(getClaimUrl(report), 'Claim URL')}
                                startIcon={<ContentCopyIcon />}
                              >
                                Copy claim link URL
                              </Button>
                            ),
                            <Button
                              sx={{ padding: '4px 10px' }}
                              variant="contained"
                              key={`view-summary-${report.uid}`}
                              onClick={() => handleRowClick(report)}
                              startIcon={<VisibilityIcon />}
                            >
                              View summary
                            </Button>,
                            <Tooltip
                              title={moreActionsDisabled ? 'No actions available' : 'More actions'}
                              arrow
                              placement="top"
                              key={`more-button-${report.uid}`}
                            >
                              <Box
                                sx={{
                                  cursor: moreActionsDisabled ? 'not-allowed' : 'pointer',
                                  minWidth: 0,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  padding: '5px',
                                  borderRadius: '50%',
                                  color: 'action.active',
                                  transition: 'background-color 0.2s',
                                  '&:hover': {
                                    backgroundColor: moreActionsDisabled ? 'transparent' : theme.palette.grey[100],
                                  },
                                  '&:active': {
                                    backgroundColor: moreActionsDisabled ? 'transparent' : theme.palette.grey[100],
                                  },
                                }}
                                onClick={moreActionsDisabled ? undefined : (e) => handleMenuOpen(e, report)}
                              >
                                <MoreHorizIcon color={moreActionsDisabled ? 'disabled' : 'action'} />
                              </Box>
                            </Tooltip>,
                          ]}
                        />
                      ),
                    };
                  })}
                />
              </Box>
            </div>
          </PageContent>
        );
      },
      [
        filtersRef,
        handleRowClick,
        handleMenuOpen,
        setCopied,
        handleClearFilters,
        theme.palette.grey,
        isMoreActionsDisabled,
      ],
    );

    return (
      <Box className={styles.gridViewContainer}>
        <PaginatedList<IReport>
          ref={paginatedListRef}
          items={items}
          pageSize={pageSize}
          totalCount={totalCount}
          onChange={onChange}
          isFinalItems={isFinalReport}
          defaultValue={page}
        >
          {({ items: reports, pagination: Pagination }) => {
            const visibleReports = !isReportsFetching && reports.length > 0;

            return (
              <>
                {isReportsFetching ? <TableViewLoader /> : renderContent(reports)}
                {visibleReports && (
                  <PaginationControls
                    pageSize={pageSize}
                    options={PER_PAGE_OPTIONS}
                    onPageSizeChange={onPageSizeChange!}
                    PaginationComponent={Pagination}
                  />
                )}
              </>
            );
          }}
        </PaginatedList>
        <ReportsContextMenu
          anchorEl={menuAnchorEl}
          selectedItem={selectedItem}
          isNonCancelingReportStatus={isNonCancelingReportStatus}
          canCancelReport={canCancelReport}
          canDeleteReport={canDeleteReport}
          canCopyShippingUrl={canCopyShippingUrl}
          canCopyTrackingUrl={canCopyTrackingUrl}
          onClose={handleMenuClose}
          onCancelReport={handleCancelReport}
          onDeleteReport={handleDeleteReport}
        />
      </Box>
    );
  },
);

export default ReportsGridView;
